<template>
  <div class="tw-relative">
    <div
      v-if="disabled"
      class="tw-absolute tw-left-0 tw-top-0 tw-z-10 tw-h-full tw-w-full tw-bg-white/80"
    ></div>
    <button
      type="button"
      class="tw-my-[8px] tw-flex tw-w-full tw-cursor-pointer tw-items-center tw-justify-between tw-rounded-[8px] tw-py-[16px] focus-visible:tw-ring focus-visible:tw-ring-sky-200 focus-visible:tw-ring-opacity-50 focus-visible:tw-ring-offset-2"
      data-testid="mobile-stacked-filters-area-accordion"
      :aria-expanded="isOpen.toString()"
      aria-controls="area-options"
      @click="toggleOpen"
    >
      <span
        class="tw-text-[16px] tw-font-semibold tw-leading-none tw-text-primary-grey"
      >
        Area
      </span>

      <div class="tw-flex tw-items-center tw-gap-2">
        <svg
          :class="{ 'tw-rotate-180': isOpen }"
          xmlns="http://www.w3.org/2000/svg"
          class="tw-h-4 tw-w-4 tw-text-[#3B4143]"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </div>
    </button>

    <div
      v-if="isOpen"
      id="area-options"
      role="radiogroup"
      aria-label="Choose area"
      class="tw-mb-[24px] tw-flex tw-flex-col tw-gap-y-[24px] tw-pt-[16px] tw-text-[16px] tw-font-normal tw-leading-[21px] tw-text-[#3B4143]"
      @keydown.down.prevent="focusNext"
      @keydown.up.prevent="focusPrev"
      @keydown.esc.prevent="closeOptions"
    >
      <!-- Any option -->
      <div
        ref="options"
        role="radio"
        :aria-checked="selectedAreaName === '' ? 'true' : 'false'"
        :tabindex="focusedIndex === -1 ? '0' : '-1'"
        class="tw-flex tw-h-[24px] tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[8px] tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-sky-200 focus-visible:tw-ring-opacity-50 focus-visible:tw-ring-offset-2"
        @click.stop="selectAny"
        @keydown.enter.prevent="selectAny"
        @keydown.space.prevent="selectAny"
        @focus="focusedIndex = -1"
      >
        <span class="tw-h-[24px] tw-flex-1">Any</span>

        <span
          :class="`tw-flex tw-h-[24px] tw-w-[24px] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-500 ${
            selectedAreaName === ''
              ? 'tw-border-uhblue-500 tw-bg-uhblue-500'
              : 'tw-bg-white'
          }`"
        >
          <span
            v-if="selectedAreaName === ''"
            class="tw-h-[12px] tw-w-[12px] tw-rounded-full tw-bg-white"
          ></span>
        </span>
      </div>

      <div
        v-for="(area, index) in areas"
        :key="index"
        ref="options"
        role="radio"
        :aria-checked="selectedAreaName === area.name ? 'true' : 'false'"
        :tabindex="focusedIndex === index ? '0' : '-1'"
        class="tw-flex tw-h-[24px] tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-[8px] tw-outline-none focus-visible:tw-ring focus-visible:tw-ring-sky-200 focus-visible:tw-ring-opacity-50 focus-visible:tw-ring-offset-2"
        data-testid="mobile-stacked-filters-area"
        @click.stop="toggleSelection(area.name)"
        @keydown.enter.prevent="toggleSelection(area.name)"
        @keydown.space.prevent="toggleSelection(area.name)"
        @focus="focusedIndex = index"
      >
        <span class="tw-h-[24px] tw-flex-1">
          <span>{{ area.name }}</span>
          <span v-if="showCount"> ({{ area.count ?? 0 }})</span>
        </span>

        <span
          :class="`tw-flex tw-h-[24px] tw-w-[24px] tw-items-center tw-justify-center tw-rounded-full tw-border tw-border-gray-500 ${
            selectedAreaName === area.name
              ? 'tw-border-uhblue-500 tw-bg-uhblue-500'
              : 'tw-bg-white'
          }`"
        >
          <span
            v-if="selectedAreaName === area.name"
            class="tw-h-[12px] tw-w-[12px] tw-rounded-full tw-bg-white"
          ></span>
        </span>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'StackedLocationAreaFilter',
  props: {
    areas: {
      type: Array,
      required: true,
      default: () => [],
    },
    currentArea: {
      type: Object,
      required: false,
      default: null,
    },
    showCount: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      isOpen: false,
      selectedAreaName: '',
      focusedIndex: 0,
    };
  },
  watch: {
    currentArea(newVal) {
      this.selectedAreaName = newVal ? newVal.name : '';
      // Only open accordion if a specific area is selected (not 'Any')
      if (newVal) {
        this.isOpen = true;
      }
    },
  },
  mounted() {
    if (this.currentArea) {
      this.selectedAreaName = this.currentArea.name;
      // Only open accordion if a specific area is already selected when component mounts
      this.isOpen = true;
    } else {
      // Default to 'Any' selection (empty string)
      this.selectedAreaName = '';
    }
  },
  methods: {
    toggleOpen() {
      this.isOpen = !this.isOpen;

      if (this.isOpen) {
        this.$nextTick(() => {
          if (this.selectedAreaName === '') {
            // 'Any' is selected
            this.focusedIndex = -1;
          } else {
            // Find the selected area
            const selectedIndex = this.areas.findIndex(
              (u) => u.name === this.selectedAreaName
            );
            this.focusedIndex = selectedIndex >= 0 ? selectedIndex : -1;
          }
          this.focusOption(this.focusedIndex);
        });
      }
    },
    toggleSelection(name) {
      if (this.selectedAreaName === name) {
        this.selectedAreaName = '';
        this.$emit('set-area', null);
      } else {
        this.selectedAreaName = name;
        const selectedArea = this.areas.find((u) => u.name === name);
        this.$emit('set-area', selectedArea || null);

        // Keep accordion open when an area is selected
        this.$nextTick(() => {
          this.isOpen = true;
        });
      }
    },
    selectAny() {
      this.selectedAreaName = '';
      this.$emit('set-area', null);
      // Don't keep accordion open for 'Any' selection - let it close naturally
    },
    focusOption(index) {
      const optionEls = this.$refs.options;

      if (optionEls) {
        if (index === -1) {
          // Focus the 'Any' option (first element)
          if (optionEls[0]) {
            optionEls[0].focus();
          }
        } else if (optionEls[index + 1]) {
          // Focus specific area option (+1 because 'Any' is at index 0)
          optionEls[index + 1].focus();
        }
      }
    },
    focusNext() {
      // -1 = 'Any', 0 to areas.length-1 = specific areas
      if (this.focusedIndex === -1) {
        this.focusedIndex = 0;
      } else if (this.focusedIndex === this.areas.length - 1) {
        this.focusedIndex = -1;
      } else {
        this.focusedIndex++;
      }
      this.focusOption(this.focusedIndex);
    },
    focusPrev() {
      // -1 = 'Any', 0 to areas.length-1 = specific areas
      if (this.focusedIndex === -1) {
        this.focusedIndex = this.areas.length - 1;
      } else if (this.focusedIndex === 0) {
        this.focusedIndex = -1;
      } else {
        this.focusedIndex--;
      }
      this.focusOption(this.focusedIndex);
    },
    closeOptions() {
      this.isOpen = false;

      this.$nextTick(() => {
        this.$el.querySelector('button').focus();
      });
    },
  },
};
</script>
